import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import {
  ShoppingBagIcon,
  CurrencyDollarIcon,
  ClockIcon,
  ShieldCheckIcon,
  PlusCircleIcon,
  WalletIcon,
  CreditCardIcon,
  Cog6ToothIcon,
  ChartBarIcon,
  UserCircleIcon,
  BellIcon,
  ArrowTrendingUpIcon,
  FireIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import api from '../services/api';

function Dashboard() {
  const navigate = useNavigate();
  const [userName, setUserName] = useState('');
  const [userAvatar, setUserAvatar] = useState('');
  
  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        setUserName(user.name || user.username || 'Usuario');
        setUserAvatar(user.avatar || '');
      } catch (e) {}
    }
  }, []);
  
  const { data: stats, isLoading } = useQuery('stats', async () => {
    const response = await api.get('/stats');
    return response.data;
  }, {
    refetchInterval: 30000 // Refrescar cada 30 segundos
  });

  const quickActions = [
    {
      icon: PlusCircleIcon,
      title: 'Agregar Cookie',
      subtitle: 'Nueva cookie Amazon',
      path: '/add-cookie',
      color: '#ff9900',
      bg: '#ff990015',
      border: '#ff990030'
    },
    {
      icon: CreditCardIcon,
      title: 'Tarjetas',
      subtitle: 'Gestionar tarjetas',
      path: '/cards',
      color: '#ff00ff',
      bg: '#ff00ff15',
      border: '#ff00ff30'
    },
    {
      icon: ShoppingBagIcon,
      title: 'Cookies',
      subtitle: `${stats?.stats?.activeCookies || 0} activas`,
      path: '/cookies',
      color: '#00ffff',
      bg: '#00ffff15',
      border: '#00ffff30'
    },
    {
      icon: ChartBarIcon,
      title: 'Estadísticas',
      subtitle: 'Ver reportes',
      path: '/statistics',
      color: '#ccff00',
      bg: '#ccff0015',
      border: '#ccff0030'
    }
  ];

  const getUserInitial = () => {
    if (userAvatar) return null;
    if (!userName) return 'U';
    return userName.charAt(0).toUpperCase();
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      backgroundColor: '#000000',
      paddingBottom: '6rem'
    }}>
      {/* Header Mejorado */}
      <div style={{
        background: 'linear-gradient(180deg, #0a0a0a 0%, #000000 100%)',
        borderBottom: '1px solid #1a1a1a',
        padding: '1rem 1.25rem',
        position: 'sticky',
        top: 0,
        zIndex: 40
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          {/* Logo y saludo */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            {/* Logo */}
            <div style={{
              width: '42px',
              height: '42px',
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #ff9900, #ff6600)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 15px rgba(255,153,0,0.3)',
              flexShrink: 0
            }}>
              <FireIcon style={{ width: '1.4rem', height: '1.4rem', color: '#000' }} />
            </div>
            
            <div>
              <h1 style={{
                fontSize: '1.1rem',
                fontWeight: '700',
                color: '#ffffff',
                margin: 0,
                lineHeight: '1.2'
              }}>
                theblacklist
              </h1>
              <p style={{
                fontSize: '0.7rem',
                color: '#ff9900',
                margin: 0,
                fontWeight: '500'
              }}>
                Hola, {userName || 'Usuario'} 👋
              </p>
            </div>
          </div>

          {/* Botones derecha */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            {/* Botón de perfil */}
            <button
              onClick={() => navigate('/profile')}
              style={{
                width: '38px',
                height: '38px',
                borderRadius: '50%',
                border: '2px solid #222',
                background: userAvatar 
                  ? `url(${userAvatar}) center/cover` 
                  : 'linear-gradient(135deg, #00ffff, #0066ff)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontSize: '0.9rem',
                fontWeight: 'bold',
                overflow: 'hidden',
                flexShrink: 0
              }}
            >
              {!userAvatar && getUserInitial()}
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ padding: '0 1rem' }}>
        {/* Welcome Card */}
        <div style={{
          background: 'linear-gradient(135deg, #111111, #0a0a0a)',
          border: '1px solid #222',
          borderRadius: '1.25rem',
          padding: '1.25rem',
          marginTop: '1rem',
          marginBottom: '1.25rem',
          display: 'flex',
          alignItems: 'center',
          gap: '1rem'
        }}>
          <div style={{
            width: '50px',
            height: '50px',
            borderRadius: '14px',
            background: 'linear-gradient(135deg, #ff990020, #ff660020)',
            border: '1px solid #ff990030',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}>
            <SparklesIcon style={{ width: '1.5rem', height: '1.5rem', color: '#ff9900' }} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '0.85rem', fontWeight: '600', color: '#fff', marginBottom: '0.2rem' }}>
              Panel de Control
            </div>
            <div style={{ fontSize: '0.7rem', color: '#9ca3af' }}>
              Gestiona tus cookies, tarjetas y más
            </div>
          </div>
          <ArrowTrendingUpIcon style={{ width: '1.5rem', color: '#ccff00', opacity: 0.5 }} />
        </div>

        {/* Stats Cards */}
        {!isLoading && stats && (
          <div style={{ marginBottom: '1.5rem' }}>
            {/* Cookies Activas - Card Principal */}
            <div style={{
              background: 'linear-gradient(135deg, #1a1a1a, #111111)',
              border: '1px solid #ff990020',
              borderRadius: '1.25rem',
              padding: '1.25rem',
              marginBottom: '0.75rem',
              position: 'relative',
              overflow: 'hidden'
            }}>
              {/* Fondo decorativo */}
              <div style={{
                position: 'absolute',
                top: '-20px',
                right: '-20px',
                width: '100px',
                height: '100px',
                borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(255,153,0,0.1) 0%, transparent 70%)',
                pointerEvents: 'none'
              }} />
              
              <div style={{ position: 'relative', zIndex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
                  <div style={{
                    width: '44px',
                    height: '44px',
                    borderRadius: '12px',
                    background: 'linear-gradient(135deg, #ff9900, #ff6600)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 4px 15px rgba(255,153,0,0.2)'
                  }}>
                    <ShoppingBagIcon style={{ width: '1.3rem', height: '1.3rem', color: '#fff' }} />
                  </div>
                  <div>
                    <div style={{ fontSize: '0.7rem', color: '#ff9900', textTransform: 'uppercase', fontWeight: '600', letterSpacing: '0.05em' }}>
                      Cookies Amazon
                    </div>
                    <div style={{ fontSize: '1.75rem', fontWeight: 'bold', color: '#fff' }}>
                      {stats.stats.totalCookies}
                    </div>
                  </div>
                </div>
                
                <div style={{ display: 'flex', gap: '1rem', fontSize: '0.7rem' }}>
                  <span style={{ 
                    background: '#ccff0015', 
                    color: '#ccff00', 
                    padding: '0.25rem 0.6rem', 
                    borderRadius: '0.5rem',
                    fontWeight: '600'
                  }}>
                    {stats.stats.activeCookies} activas
                  </span>
                  <span style={{ 
                    background: '#ff003315', 
                    color: '#ff3366', 
                    padding: '0.25rem 0.6rem', 
                    borderRadius: '0.5rem',
                    fontWeight: '600'
                  }}>
                    {stats.stats.totalCookies - stats.stats.activeCookies} inactivas
                  </span>
                </div>
              </div>
            </div>

            {/* Grid de 2 columnas */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              {/* Monto */}
              <div style={{
                background: '#111111',
                border: '1px solid #222',
                borderRadius: '1rem',
                padding: '1rem',
              }}>
                <div style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '10px',
                  background: '#00ffff15',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '0.5rem'
                }}>
                  <CurrencyDollarIcon style={{ width: '1.1rem', height: '1.1rem', color: '#00ffff' }} />
                </div>
                <div style={{ fontSize: '0.65rem', color: '#9ca3af', textTransform: 'uppercase', marginBottom: '0.2rem' }}>
                  Monto Total
                </div>
                <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#fff' }}>
                  ${stats.stats.totalAmount}
                </div>
              </div>

              {/* Tarjetas */}
              <div style={{
                background: '#111111',
                border: '1px solid #222',
                borderRadius: '1rem',
                padding: '1rem',
              }}>
                <div style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '10px',
                  background: '#ff00ff15',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '0.5rem'
                }}>
                  <CreditCardIcon style={{ width: '1.1rem', height: '1.1rem', color: '#ff00ff' }} />
                </div>
                <div style={{ fontSize: '0.65rem', color: '#9ca3af', textTransform: 'uppercase', marginBottom: '0.2rem' }}>
                  Tarjetas
                </div>
                <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#fff' }}>
                  {stats.stats.totalCards || 0}
                </div>
              </div>
            </div>

            {/* Barra de uso */}
            <div style={{
              background: '#111111',
              border: '1px solid #222',
              borderRadius: '1rem',
              padding: '1rem',
              marginTop: '0.75rem'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                <span style={{ fontSize: '0.7rem', color: '#9ca3af' }}>Usos</span>
                <span style={{ fontSize: '0.7rem', fontWeight: 'bold', color: '#fff' }}>
                  {stats.stats.totalUses}/{stats.stats.totalPossibleUses}
                </span>
              </div>
              <div style={{ 
                width: '100%', 
                height: '6px', 
                background: '#1a1a1a', 
                borderRadius: '3px', 
                overflow: 'hidden' 
              }}>
                <div style={{ 
                  height: '100%', 
                  width: `${stats.stats.usagePercentage || 0}%`, 
                  background: 'linear-gradient(90deg, #00ffff, #ff00ff, #ff9900)',
                  borderRadius: '3px',
                  transition: 'width 0.5s ease'
                }} />
              </div>
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ 
            fontSize: '0.95rem', 
            fontWeight: '600', 
            color: '#fff', 
            marginBottom: '0.75rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <span style={{
              width: '4px',
              height: '16px',
              background: 'linear-gradient(180deg, #00ffff, #ff00ff)',
              borderRadius: '2px',
              display: 'inline-block'
            }} />
            Acciones Rápidas
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.75rem' }}>
            {quickActions.map((action) => (
              <button
                key={action.title}
                onClick={() => navigate(action.path)}
                style={{
                  background: action.bg,
                  border: `1px solid ${action.border}`,
                  borderRadius: '1rem',
                  padding: '1rem 0.75rem',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '0.5rem',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  width: '100%'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = `0 8px 25px ${action.border}`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div style={{
                  width: '42px',
                  height: '42px',
                  borderRadius: '12px',
                  background: `${action.color}15`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <action.icon style={{ width: '1.3rem', height: '1.3rem', color: action.color }} />
                </div>
                <span style={{ fontWeight: '600', fontSize: '0.8rem', color: '#fff' }}>{action.title}</span>
                <span style={{ fontSize: '0.65rem', color: '#9ca3af', textAlign: 'center' }}>{action.subtitle}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Actividad Reciente */}
        <div style={{ marginBottom: '1rem' }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginBottom: '0.75rem' 
          }}>
            <h3 style={{ 
              fontSize: '0.95rem', 
              fontWeight: '600', 
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <span style={{
                width: '4px',
                height: '16px',
                background: 'linear-gradient(180deg, #ff9900, #ff6600)',
                borderRadius: '2px',
                display: 'inline-block'
              }} />
              Actividad Reciente
            </h3>
            <button 
              onClick={() => navigate('/cookies')}
              style={{ color: '#00ffff', fontSize: '0.75rem', background: 'none', border: 'none', cursor: 'pointer' }}
            >
              Ver todo →
            </button>
          </div>
          
          {stats?.stats?.activeCookies > 0 ? (
            <div style={{
              background: '#111111',
              border: '1px solid #222',
              borderRadius: '1rem',
              padding: '1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem'
            }}>
              <div style={{ 
                width: '8px', 
                height: '8px', 
                borderRadius: '50%', 
                background: '#ccff00', 
                boxShadow: '0 0 8px #ccff00',
                flexShrink: 0
              }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: '600', color: '#fff', marginBottom: '0.2rem', fontSize: '0.85rem' }}>
                  Cookies activas disponibles
                </div>
                <div style={{ display: 'flex', gap: '0.75rem', fontSize: '0.7rem', color: '#9ca3af' }}>
                  <span>{stats.stats.activeCookies} disponibles</span>
                  <span style={{ fontWeight: '700', color: '#ccff00' }}>${stats.stats.pendingAmount || 0} pendiente</span>
                </div>
              </div>
              <button 
                onClick={() => navigate('/cookies?status=activa')}
                style={{
                  padding: '0.4rem 0.9rem',
                  borderRadius: '2rem',
                  fontSize: '0.7rem',
                  fontWeight: '600',
                  background: 'rgba(0, 255, 255, 0.1)',
                  color: '#00ffff',
                  border: '1px solid rgba(0, 255, 255, 0.2)',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(0, 255, 255, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(0, 255, 255, 0.1)';
                }}
              >
                Pagar
              </button>
            </div>
          ) : (
            <div style={{ 
              textAlign: 'center', 
              padding: '2.5rem 1.5rem', 
              color: '#9ca3af',
              background: '#111111',
              borderRadius: '1rem',
              border: '1px solid #222'
            }}>
              <div style={{
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                background: '#1a1a1a',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1rem'
              }}>
                <PlusCircleIcon style={{ width: '2rem', height: '2rem', color: '#ff9900', opacity: 0.5 }} />
              </div>
              <p style={{ marginBottom: '1rem' }}>No hay actividad reciente</p>
              <button
                onClick={() => navigate('/add-cookie')}
                style={{
                  padding: '0.7rem 1.5rem',
                  background: 'linear-gradient(135deg, #ff9900, #ff6600)',
                  border: 'none',
                  borderRadius: '2rem',
                  color: '#000',
                  fontWeight: '700',
                  fontSize: '0.85rem',
                  cursor: 'pointer'
                }}
              >
                Agregar primera cookie Amazon
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Estilos */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        .stat-card {
          animation: fadeIn 0.3s ease;
        }
      `}</style>
    </div>
  );
}

export default Dashboard;